import CIcon from '@coreui/icons-react';
import DOMPurify from 'dompurify';
import { CButton } from '@coreui/react';
import PropTypes from 'prop-types';
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { escapeRegExp, useOutsideHandling } from '../../../../utils';
import { CInput } from '../../../migration/CInput';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import CTooltip from '../../../migration/CTooltip';
import { debounce } from 'lodash';

const Dropdown = ({ children, isOpen, target, onClose, fieldFull }) => (
     <div className='select-wrapper'>
          {target}
          {fieldFull ? isOpen ? <Menu1>{children}</Menu1> : null : isOpen ? <Menu>{children}</Menu> : null}
          {isOpen ? <Blanket onClick={onClose} /> : null}
     </div>
);

const Menu1 = (props) => {
     return (
          <div
               style={{
                    borderRadius: 4,
                    marginTop: 8,
                    position: 'absolute',
                    zIndex: 10,
                    cursor: 'default',
               }}
               {...props}
          />
     );
};
const Menu = (props) => {
     return (
          <div
               style={{
                    borderRadius: 4,
                    marginTop: 8,
                    position: 'absolute',
                    zIndex: 10,
                    width: '100%',
                    cursor: 'default',
               }}
               {...props}
          />
     );
};

const Blanket = (props) => (
     <div
          style={{
               bottom: 0,
               left: 0,
               top: 0,
               right: 0,
               position: 'absolute',
               zIndex: 2,
          }}
          {...props}
     />
);

function SortableItem({ index, item, onRemove }) {
     const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: item.value });

     const style = transform
          ? {
                 transform: CSS.Translate.toString(transform),
                 transition: transition,
                 pointerEvents: 'none',
                 display: 'flex',
                 alignItems: 'center',
                 justifyContent: 'space-between',
                 padding: '0px',
            }
          : {
                 display: 'flex',
                 alignItems: 'center',
                 justifyContent: 'space-between',
                 padding: '0px',
            };

     return (
          <Fragment key={index}>
               {item?.value && (
                    <li ref={setNodeRef} style={style} {...attributes}>
                         <span
                              {...listeners}
                              style={{
                                   padding: '3.5px 21px 3.5px 6px',
                              }}
                         >
                              {item.description ? (
                                   <CTooltip content={item.description}>
                                        <span>{item.label}</span>
                                   </CTooltip>
                              ) : (
                                   <>{item.label}</>
                              )}
                         </span>{' '}
                         {/* Attach drag listeners only to the label */}
                         <CButton
                              onClick={(e) => {
                                   e.stopPropagation(); // Prevent the click event from bubbling
                                   onRemove(index); // Call the onRemove handler
                              }}
                              onMouseDown={(e) => e.stopPropagation()} // Prevent the button from starting a drag
                              className='icon-remove-multi'
                         >
                              <CIcon icon='iconRemoveMultiSelect' width={10} height={10} />
                         </CButton>
                    </li>
               )}
          </Fragment>
     );
}

const SelectSearch = (props) => {
     const {
          id,
          isBlur,
          placeholder,
          options,
          onChange,
          value,
          isDisabled,
          className,
          classNameWrapper,
          selectInputType,
          displayPlaceholder,
          fieldFull,
          isMulti,
          error,
          setShowPopupCreateVariables,
          setStorageType,
          setVariableKeyId,
          valueFrom,
          groupValue,
     } = props;

     const wrapperRef = useRef(null);
     const filterInputRef = useRef(null);
     const valueInputRef = useRef(null);
     const [isOpen, setIsOpen] = useState(false);
     const [initValue, setInitValue] = useState(value);
     const inputValue = initValue?.label && !displayPlaceholder ? initValue.label : placeholder;
     const [variableFilter, setVariableFilter] = useState(''); // Current text in the insert data layer variable box filter
     const [openTypeVariable, setOpenTypeVariable] = useState({});
     const [showScroll, setShowScroll] = useState(false);
     let variableFilterLowerCase = variableFilter.toLowerCase();
     // Automatic variable options which are displayed in insert data layer variable box
     let variableOptionsFound = options.filter((option) => option.label !== '' && option.label.toLowerCase().includes(variableFilterLowerCase));
     const [variableOptions, setVariableOptions] = useState([]);
     const itemsLoadMore = 20;
     const valueField = value?.label || '';

     useEffect(() => {
          if (wrapperRef.current) {
               if (wrapperRef.current.scrollHeight > wrapperRef.current.clientHeight) {
                    setShowScroll(true);
               } else {
                    setShowScroll(false);
               }
          }
     }, [openTypeVariable]);

     useEffect(() => {
          if (value) {
               setInitValue(value);
          } else {
               setInitValue(null);
          }
     }, [value]);

     const clickOutside = useCallback(() => {
          setIsOpen(false);
          setOpenTypeVariable({});
          setShowScroll(false);
          setVariableFilter('');
     }, []);

     useOutsideHandling(wrapperRef, clickOutside, 'mouseup');

     useEffect(() => {
          if (!isMulti) {
               if (isOpen && filterInputRef.current) {
                    setTimeout(() => {
                         filterInputRef.current.focus();
                    }, 0);
               }
               if (!isOpen && valueInputRef.current) valueInputRef.current.focus();
          }
     }, [isMulti, isOpen]);

     const toggleOpen = (e) => {
          const disableInput = e ? !e.target.classList.contains('disable-select') : true;
          if (disableInput)
               setTimeout(() => {
                    setIsOpen(!isOpen);
                    if (valueInputRef.current) valueInputRef.current.readOnly = false;
               }, 1);
     };

     useEffect(() => {
          setVariableFilter('');
     }, [groupValue]); // eslint-disable-line

     const onSelectChange = (e) => {
          if (isMulti) {
               let newValue = [];
               if (value?.length > 0) {
                    newValue = [...value, e];
               } else {
                    newValue = [e];
               }
               const { onChange, name, hasChange } = props;
               onChange(name, newValue);
               if (hasChange) {
                    hasChange();
               }
          } else {
               valueInputRef.current.focus();
               toggleOpen();
               setInitValue(e);
               onChange(e);
          }
     };

     useEffect(() => {
          if (variableFilter) {
               searchItems(options, variableFilter);

               return () => {
                    searchItems.cancel();
               };
          } else {
               const newVariableOptions = options.filter((item) => item.label !== '').slice(0, itemsLoadMore);
               setVariableOptions(newVariableOptions);
               if (wrapperRef.current) {
                    wrapperRef.current.scrollTo({ top: 0 });
               }
          }
     }, [variableFilter, options]); // eslint-disable-line

     const searchItems = useMemo(() => {
          return debounce((items, query) => {
               if (query) {
                    const filteredItems = items.filter((item) => item.label !== '' && item.label.toLowerCase().includes(query.toLowerCase())).slice(0, itemsLoadMore);
                    setVariableOptions(filteredItems);
               }
          }, 200);
     }, []);

     const handleSearch = (e) => {
          setVariableFilter(e);
     };

     const handleRemoveVariable = (i) => {
          const newValue = [...value];
          newValue.splice(i, 1);
          const { onChange, name, hasChange } = props;
          onChange(name, newValue);
          if (hasChange) {
               hasChange();
          }
          setTimeout(() => {
               setIsOpen(false);
          }, 2);
     };

     const handleCheckShowData = (val, option) => {
          const checkVal = val.find((item) => {
               if (item?.label && item.label === option.label) {
                    return true;
               }
               return false;
          });
          return checkVal;
     };

     const handleClick = (option) => {
          if (option.isCustom) {
               setShowPopupCreateVariables(true);
               setStorageType(valueFrom);
               setVariableKeyId(id);
          } else {
               onSelectChange(option);
          }
     };

     const loadMoreItems = () => {
          const currentLength = variableOptions.length;
          const moreItems = variableOptionsFound?.slice(currentLength, currentLength + itemsLoadMore);

          if (variableOptions.length + moreItems.length <= variableOptionsFound.length) {
               setVariableOptions((prevItems) => [...prevItems, ...moreItems]);
          }
     };

     const handleScroll = () => {
          if (wrapperRef.current && wrapperRef.current.scrollTop + wrapperRef.current.clientHeight >= wrapperRef.current.scrollHeight) {
               loadMoreItems();
          }
     };

     const noOptionsFound = !Boolean(variableOptionsFound.length);

     return (
          <div
               className={`select-insert-variable ${classNameWrapper} ${isBlur ? ' select-react-customize blur' : ' select-react-customize'}${
                    !value?.value ? ' not-selected' : ''
               }`}
               ref={wrapperRef}
          >
               <Dropdown
                    fieldFull={fieldFull}
                    className={className}
                    isOpen={isOpen}
                    onClose={toggleOpen}
                    target={
                         selectInputType ? (
                              <CInput
                                   onClick={() => toggleOpen()}
                                   readOnly
                                   value={inputValue}
                                   disabled={isDisabled}
                                   className={`${isDisabled ? 'disable-select' : ''}`}
                              />
                         ) : (
                              <>
                                   {isMulti ? (
                                        <>
                                             <div
                                                  className={`stretch-container select-multiple-variable-field form-control ${
                                                       isDisabled ? 'disabled' : ''
                                                  } ${isDisabled ? 'disable-select' : ''} `}
                                                  onClick={toggleOpen}
                                             >
                                                  {value?.length > 0 ? (
                                                       <ul>
                                                            {value.map((item, index) => (
                                                                 <SortableItem
                                                                      index={index}
                                                                      key={item.value}
                                                                      item={item}
                                                                      onRemove={handleRemoveVariable}
                                                                 />
                                                            ))}
                                                       </ul>
                                                  ) : (
                                                       <div className='place-holder-select'>{'Select items'}</div>
                                                  )}
                                             </div>
                                        </>
                                   ) : (
                                        <input
                                             type='text'
                                             className={`stretch-container form-control ${isDisabled ? 'disable-select' : ''} `}
                                             onClick={toggleOpen}
                                             ref={valueInputRef}
                                             value={inputValue ? inputValue.replace('<br>', '') : inputValue}
                                             readOnly
                                        ></input>
                                   )}
                              </>
                         )
                    }
               >
                    <div className={`insert-variable-dropdown`}>
                         <ul
                              ref={wrapperRef}
                              className={`${options.length === 0 ? 'value-no-options-available no-options' : ''} ${showScroll ? 'have-scroll w-100' : ''}`}
                              onScroll={handleScroll}
                         >
                              <li className='variable-filter'>
                                   <CInput
                                        innerRef={filterInputRef}
                                        type='text'
                                        value={variableFilter}
                                        onChange={(e) => handleSearch(e.target.value)}
                                        placeholder='Select or type to search'
                                   />
                              </li>
                              {noOptionsFound ? (
                                   <li className='no-options border-bottom d-flex align-items-center justify-content-center text-center'>No options</li>
                              ) : (
                                   variableOptions.length > 0 && (
                                        <>
                                             {variableOptions.map((option, i) => {
                                                  const activeValue = valueField === option.label;
                                                  const re = new RegExp(escapeRegExp(variableFilter), 'gi');
                                                  let newVariable = option.label;
                                                  if (variableFilter) {
                                                       newVariable = newVariable.replace(re, (variable) => `<strong>${variable}</strong>`);
                                                  }
                                                  let checkShowValue = false;
                                                  if (isMulti) {
                                                       checkShowValue = handleCheckShowData(value, option);
                                                  }
                                                  const sanitizedNewVariable = DOMPurify.sanitize(`${newVariable}`);
                                                  return (
                                                       <Fragment key={i}>
                                                            {!checkShowValue ? (
                                                                 <li
                                                                      className={`border-bottom d-flex align-items-center ${
                                                                           activeValue ? 'active-variable' : ''
                                                                      } ${option.isCustom ? 'custom' : ''}`}
                                                                      key={option.label}
                                                                      onClick={() => handleClick(option)}
                                                                 >
                                                                      <span
                                                                           className='text-break'
                                                                           dangerouslySetInnerHTML={{
                                                                                __html: sanitizedNewVariable,
                                                                           }}
                                                                      ></span>
                                                                 </li>
                                                            ) : (
                                                                 ''
                                                            )}
                                                       </Fragment>
                                                  );
                                             })}
                                        </>
                                   )
                              )}
                         </ul>
                    </div>
               </Dropdown>
               {error && typeof error === 'string' ? <p style={{ color: '#e55353', fontSize: '80%' }}>{error}</p> : null}
          </div>
     );
};
SelectSearch.propTypes = {
     id: PropTypes.string,
     isBlur: PropTypes.bool,
     placeholder: PropTypes.string,
     options: PropTypes.array,
     onChange: PropTypes.func,
     value: PropTypes.object,
     isDisabled: PropTypes.bool,
     className: PropTypes.string,
     classNamePrefix: PropTypes.string,
     classNameWrapper: PropTypes.string,
     selectInputType: PropTypes.bool,
     displayPlaceholder: PropTypes.bool,
     filterOption: PropTypes.func,
     selectStyles: PropTypes.object,
     isMulti: PropTypes.bool,
};

SortableItem.propTypes = {
     index: PropTypes.number,
     item: PropTypes.shape({
          value: PropTypes.any,
          label: PropTypes.string,
          description: PropTypes.string,
     }),
     onRemove: PropTypes.func,
};

export default SelectSearch;
